import React from "react";
import { Box, Typography, Stack, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import AccountCircleRoundedIcon from "@mui/icons-material/AccountCircleRounded";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { formatPhone } from "../../../utils/phoneUtils";


const CallHeaderContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  marginLeft: theme.spacing(-1),
  marginRight: theme.spacing(-1),
}));

const CallerInfo = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isActive",
})(({ theme, isActive }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2, 2, 2, 1),
  borderRadius: "8px",
  backgroundColor: isActive
    ? theme.palette.secondary.dark
    : theme.palette.action.selected,
  opacity: isActive ? 1 : 0.6,
  width: "49%",
  overflow: "hidden",
  minHeight: "110px",
}));

const RecordingIndicator = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const CallerDetails = styled(Stack)({
  marginLeft: "5px",
  overflow: "hidden",
});

const CallStatus = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const TruncatedTypography = styled(Typography)(({ theme }) => ({
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  zIndex: theme.zIndex.tooltip,
  cursor: "pointer",
}));

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs
    .toString()
    .padStart(2, "0")}`;
};

const CallHeader = ({ callSessions, activeCall }) => {
  return (
    <>
      <CallStatus variant="h4" color="theme.palette.common.white">
        {activeCall?.status === "accepted"
          ? "Call in Progress..."
          : "Calling..."}
      </CallStatus>
      <CallHeaderContainer>
        {callSessions.map((call) => (
          <CallerInfo key={call.id} isActive={call.id === activeCall?.id}>
            <AccountCircleRoundedIcon sx={{ fontSize: 40 }} />
            <CallerDetails spacing={0.5}>
              <Tooltip title={call.displayName} arrow>
                <TruncatedTypography variant="subtitle1">
                  {call.displayName}
                </TruncatedTypography>
              </Tooltip>
              <Tooltip title={formatPhone(call.number)} arrow>
                <TruncatedTypography variant="body2">
                  {formatPhone(call.number)}
                </TruncatedTypography>
              </Tooltip>
              {call.status === "accepted" &&
                (call.id === activeCall?.id ? (
                  <RecordingIndicator>
                    <Typography variant="caption" color="textSecondary">
                      {formatTime(call.callDuration)}
                    </Typography>
                    {activeCall.isRecording && (
                      <FiberManualRecordIcon
                        sx={{
                          color: "error.main",
                          fontSize: 15,
                          marginBottom: "4px",
                          animation: "pulse 2s infinite",
                          "@keyframes pulse": {
                            "0%": { opacity: 1 },
                            "50%": { opacity: 0.4 },
                            "100%": { opacity: 1 },
                          },
                        }}
                      />
                    )}
                  </RecordingIndicator>
                ) : (
                  <Typography variant="caption" color="textSecondary">
                    On Hold
                  </Typography>
                ))}
            </CallerDetails>
          </CallerInfo>
        ))}
      </CallHeaderContainer>
    </>
  );
};

export default CallHeader;
